import { useRoute } from '#imports'
import { type ComputedRef, computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import type { LiveShoppingCategory } from '../constants'

// One CloudBees flag per page-type bucket (see config.ts) so each page type
// can be independently targeted/rolled out for a given user. The bucket —
// not the category — is the rollout axis: it's what the tracking zones
// (home/pdp/plp) are built on, and adding a category axis later would be
// purely additive.
const EXPERIMENT_NAME_BY_BUCKET = {
  home: 'experiment.liveShoppingOverlayHome',
  plpSearch: 'experiment.liveShoppingOverlayPlpSearch',
  pdp: 'experiment.liveShoppingOverlayPdp',
} as const satisfies Record<LiveShoppingPageBucket, `experiment.${string}`>

// Route names are stable identifiers from the route definitions (see
// `scopes/home/route-names.ts`, `scopes/product-list/route-names.ts`,
// `scopes/product-core/route-names.ts`). They're cleaner than regex on the
// path and survive locale/slug changes across SPA navigation.
//
// The PDP bucket covers two routes (see `scopes/product-core/routes.ts`):
// the variant PDP (`p/:slugV2/:uuid` → `product`) and the master/model page
// (`p/:slug` → `master-pp`), which is still a product page for shoppers, and
// where the widget's cards/ATC don't depend on the current listing.
const ROUTE_NAMES_BY_BUCKET: Record<LiveShoppingPageBucket, string[]> = {
  home: ['home'],
  plpSearch: ['landing_page'],
  pdp: ['product', 'master-pp'],
}

// Search uses a different route name but maps to the same experiment bucket
// as PLP — handled separately below since the search query param needs
// keyword inspection.
const SEARCH_ROUTE_NAME = 'search_page'

// Which video (and curated product list) a page gets, by keyword. PDP
// (`/p/...`) and PLP (`/l/...`) slugs are generated from the
// product/category name so a substring check is reliable; search pages
// don't carry the category in the path, so the `q` query param is checked
// instead. First match wins (a slug matching both patterns is not a real
// case — e.g. no slug contains both "ipad" and "macbook").
const CATEGORY_BY_KEYWORD: Array<[LiveShoppingCategory, RegExp]> = [
  ['ipad', /ipad/i],
  ['macbook', /macbook/i],
]

// Home has no product context to match on, so it gets a fixed category:
// the MacBook video, per the 2-video plan ("iPad video on iPad pages,
// MacBook video on MacBook pages + homepage").
const HOME_CATEGORY: LiveShoppingCategory = 'macbook'

export type LiveShoppingPageBucket = 'home' | 'plpSearch' | 'pdp'

export type LiveShoppingPageContext = {
  bucket: LiveShoppingPageBucket
  category: LiveShoppingCategory
}

export function detectLiveShoppingCategory(
  text: string,
): LiveShoppingCategory | undefined {
  return CATEGORY_BY_KEYWORD.find(([, pattern]) => pattern.test(text))?.[0]
}

// The page-type bucket is determined by `route.name` (stable across locale
// changes, no regex needed); the video category comes from the iPad/MacBook
// keyword narrowing on the path (PDP/PLP) or query (search). Pages that
// match neither keyword get no widget at all — Home is the only bucket not
// narrowed by a keyword.
export function getLiveShoppingPageContext(
  routeName: string | symbol | undefined,
  path: string,
  query: Record<string, unknown>,
): LiveShoppingPageContext | undefined {
  const name = typeof routeName === 'string' ? routeName : ''

  if (ROUTE_NAMES_BY_BUCKET.home.includes(name)) {
    return { bucket: 'home', category: HOME_CATEGORY }
  }

  if (name === SEARCH_ROUTE_NAME) {
    const category =
      typeof query.q === 'string'
        ? detectLiveShoppingCategory(query.q)
        : undefined

    return category ? { bucket: 'plpSearch', category } : undefined
  }

  if (ROUTE_NAMES_BY_BUCKET.plpSearch.includes(name)) {
    const category = detectLiveShoppingCategory(path)

    return category ? { bucket: 'plpSearch', category } : undefined
  }

  if (ROUTE_NAMES_BY_BUCKET.pdp.includes(name)) {
    const category = detectLiveShoppingCategory(path)

    return category ? { bucket: 'pdp', category } : undefined
  }

  return undefined
}

// Reactive route-derived context, so the widget swaps its video + curated
// product list across client-side navigation (iPad PDP → MacBook PDP)
// without a remount.
export function useLiveShoppingPageContext(): ComputedRef<
  LiveShoppingPageContext | undefined
> {
  const route = useRoute()

  return computed(() =>
    getLiveShoppingPageContext(route.name, route.path, route.query),
  )
}

/**
 * Gates the Live shopping overlay (floating widget + drawer).
 *
 * Backed by 3 CloudBees experiments (one per page-type bucket — see
 * config.ts), whose `urlPatterns` broadly cover Ireland's home/PLP/search/PDP
 * pages for the worker's cache hit ratio. The actual "Apple category"
 * narrowing (and bucket + video selection) happens here instead, reactively,
 * so the widget also shows/hides correctly across client-side navigation
 * within the SPA (not just on a fresh page load).
 */
export function useIsLiveShoppingEnabled(): ComputedRef<boolean> {
  const route = useRoute()
  const experiments = useExperiments()

  return computed(() => {
    const bucket = getLiveShoppingPageContext(
      route.name,
      route.path,
      route.query,
    )?.bucket

    return (
      bucket !== undefined &&
      experiments[EXPERIMENT_NAME_BY_BUCKET[bucket]] === 'enabled'
    )
  })
}
